import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/api";
import { useI18n } from "@/contexts/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import LanguageToggle from "@/components/LanguageToggle";
import { ArrowLeft, ExternalLink, ShieldCheck, Clock, XCircle, IndianRupee, CheckCircle2, CreditCard } from "lucide-react";
import { toast } from "sonner";

const RAZORPAY_CHECKOUT_URL = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpayCheckout() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = RAZORPAY_CHECKOUT_URL;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function PatientPay() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [status, setStatus] = useState(null);
  const [meta, setMeta] = useState(null);
  const [gatewayConfig, setGatewayConfig] = useState(null); // {razorpay_enabled, razorpay_key_id}
  const [confirming, setConfirming] = useState(false);
  const [launchingRp, setLaunchingRp] = useState(false);
  const [now, setNow] = useState(Date.now());
  const wasHiddenRef = useRef(false);
  const autoTriggeredRef = useRef(false);
  const confirmInFlightRef = useRef(false);

  // ---------- UPI fallback flow (no Razorpay configured) ----------
  const autoConfirm = async () => {
    if (confirmInFlightRef.current) return;
    confirmInFlightRef.current = true;
    setConfirming(true);
    try {
      const { data } = await axios.post(`${API}/public/payments/${appointmentId}/confirm`, {});
      if (["waiting", "checking", "completed"].includes(data.status)) {
        toast.success("Payment confirmed — generating slip");
        navigate(`/patient/slip/${appointmentId}`, { replace: true });
        return true;
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (detail.toLowerCase().includes("expired")) {
        toast.error("Payment window expired. Please try again.");
        bounceBack();
      }
    } finally {
      setConfirming(false);
      confirmInFlightRef.current = false;
    }
    return false;
  };

  const bounceBack = () => {
    const docId = sessionStorage.getItem(`upchaar_book_doctor_${appointmentId}`);
    navigate(docId ? `/patient/doctor/${docId}` : "/patient/search", { replace: true });
  };

  // ---------- Razorpay Checkout flow ----------
  const launchRazorpay = async () => {
    if (launchingRp) return;
    setLaunchingRp(true);
    try {
      const loaded = await loadRazorpayCheckout();
      if (!loaded || !window.Razorpay) {
        toast.error("Could not load Razorpay. Falling back to UPI app.");
        return;
      }
      const { data: order } = await axios.post(`${API}/payments/order`, { appointment_id: appointmentId });
      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: order.name || "UpchaarQ",
        description: order.description || `Booking ${appointmentId.slice(0, 8)}`,
        prefill: order.prefill || {},
        notes: order.notes || {},
        // Prefer UPI; Razorpay will still expose cards/netbanking
        method: { upi: true, card: true, netbanking: true, wallet: true },
        theme: { color: "#0ea5e9" },
        handler: async (resp) => {
          try {
            setConfirming(true);
            await axios.post(`${API}/payments/verify`, {
              appointment_id: appointmentId,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment verified — generating slip");
            navigate(`/patient/slip/${appointmentId}`, { replace: true });
          } catch (err) {
            toast.error(err.response?.data?.detail || "Payment verification failed");
          } finally {
            setConfirming(false);
          }
        },
        modal: {
          ondismiss: () => {
            toast.message("Payment cancelled — you can try again.");
          },
        },
      };
      const rp = new window.Razorpay(options);
      rp.on("payment.failed", (resp) => {
        toast.error(`Payment failed: ${resp.error?.description || resp.error?.code || "Unknown"}`);
      });
      rp.open();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(detail || "Could not start payment");
    } finally {
      setLaunchingRp(false);
    }
  };

  // Load intent status + handle terminal states
  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/public/payments/${appointmentId}/status`);
      setStatus(data);
      if (["waiting", "checking", "completed"].includes(data.status)) {
        navigate(`/patient/slip/${appointmentId}`, { replace: true });
      }
      if (data.status === "cancelled") {
        toast.error("Payment Failed, Please Try Again");
        bounceBack();
      }
    } catch {
      toast.error("Booking not found");
      navigate("/patient/search", { replace: true });
    }
  };

  // Mount: load meta from sessionStorage + initial status fetch + gateway config
  useEffect(() => {
    const cached = sessionStorage.getItem(`upchaar_book_${appointmentId}`);
    if (cached) setMeta(JSON.parse(cached));
    load();
    axios.get(`${API}/payments/config`).then((r) => setGatewayConfig(r.data)).catch(() => setGatewayConfig({ razorpay_enabled: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId]);

  // Auto-launch:
  //  - Razorpay Checkout modal if configured
  //  - UPI deep link otherwise
  useEffect(() => {
    if (autoTriggeredRef.current) return;
    if (gatewayConfig == null) return; // wait for config
    if (gatewayConfig.razorpay_enabled) {
      autoTriggeredRef.current = true;
      launchRazorpay();
      return;
    }
    if (meta?.upi_link) {
      autoTriggeredRef.current = true;
      const a = document.createElement("a");
      a.href = meta.upi_link;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatewayConfig, meta]);

  // Page Visibility API — UPI fallback: detect return from UPI app and auto-confirm
  useEffect(() => {
    if (gatewayConfig?.razorpay_enabled) return; // not needed with Razorpay
    const onVisChange = () => {
      if (document.hidden) {
        wasHiddenRef.current = true;
      } else if (wasHiddenRef.current && autoTriggeredRef.current) {
        wasHiddenRef.current = false;
        setTimeout(() => { autoConfirm(); }, 600);
      }
    };
    document.addEventListener("visibilitychange", onVisChange);
    window.addEventListener("focus", onVisChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisChange);
      window.removeEventListener("focus", onVisChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId, gatewayConfig]);

  // Tick countdown + polling for backend status changes
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const poll = setInterval(load, 5000);
    return () => { clearInterval(tick); clearInterval(poll); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const expiresAt = status?.payment_intent_expires_at ? new Date(status.payment_intent_expires_at).getTime() : null;
  const secondsLeft = expiresAt ? Math.max(0, Math.floor((expiresAt - now) / 1000)) : null;
  const mm = secondsLeft != null ? String(Math.floor(secondsLeft / 60)).padStart(2, "0") : "--";
  const ss = secondsLeft != null ? String(secondsLeft % 60).padStart(2, "0") : "--";

  const relaunchUpi = () => {
    if (!meta?.upi_link) return;
    window.location.href = meta.upi_link;
  };

  const cancel = async () => {
    try { await axios.post(`${API}/public/payments/${appointmentId}/cancel`); } catch { /* ignore */ }
    toast("Payment cancelled");
    bounceBack();
  };

  if (!status) return <div className="p-8 text-slate-500">{t("loading")}</div>;
  if (status.status !== "pending_payment") return null;

  const useRazorpay = gatewayConfig?.razorpay_enabled === true;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="patient-pay-page">
      <header className="px-4 sm:px-8 py-4 flex items-center justify-between bg-white border-b sticky top-0 z-10">
        <Link to={`/patient/doctor/${status.doctor_id}`} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900" data-testid="back-doctor">
          <ArrowLeft className="w-4 h-4" /> {t("back")}
        </Link>
        <LanguageToggle />
      </header>

      <main className="px-4 py-8 max-w-md mx-auto space-y-5">
        <Card className="medical-card p-6">
          <div className="flex items-center gap-2 text-amber-600">
            <Clock className="w-4 h-4" />
            <span className="text-xs tracking-[0.2em] uppercase font-semibold">Awaiting Payment</span>
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mt-2">
            {useRazorpay ? "Secure Payment" : "Complete in UPI App"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {useRazorpay
              ? "Pay via UPI, card, netbanking or wallet — verified by Razorpay. Your token releases instantly on success."
              : "Pay in PhonePe / GPay / Paytm. We will auto-detect when you return — your slip will appear instantly."}
          </p>

          <div className="grid grid-cols-2 gap-3 mt-5">
            <div className="rounded-lg bg-slate-50 border p-3">
              <div className="text-xs text-slate-500 flex items-center gap-1"><IndianRupee className="w-3 h-3" /> {t("amount")}</div>
              <div className="font-heading text-xl font-bold text-emerald-600 mt-1">₹{status.fee}</div>
            </div>
            <div className="rounded-lg bg-slate-50 border p-3">
              <div className="text-xs text-slate-500">{useRazorpay ? "Gateway" : t("upi_id")}</div>
              <div className="font-mono font-semibold text-slate-900 mt-1 truncate">
                {useRazorpay ? "Razorpay" : (meta?.upi_vpa || "—")}
              </div>
            </div>
          </div>

          <div className="mt-5 rounded-lg bg-sky-50 border border-sky-200 p-3 flex items-center justify-between">
            <div>
              <div className="text-xs text-slate-500">Window closes in</div>
              <div className="font-heading text-2xl font-bold text-sky-700 mt-0.5 font-mono">{mm}:{ss}</div>
            </div>
            <ShieldCheck className="w-8 h-8 text-sky-500" />
          </div>

          {useRazorpay ? (
            // ===== Razorpay flow =====
            confirming ? (
              <Button disabled className="w-full mt-6 h-12 text-base bg-emerald-500 text-white gap-2" data-testid="pay-confirming">
                <CheckCircle2 className="w-4 h-4 animate-pulse" /> Verifying payment…
              </Button>
            ) : (
              <Button onClick={launchRazorpay} disabled={launchingRp} className="w-full mt-6 h-12 text-base bg-sky-500 hover:bg-sky-600 text-white gap-2" data-testid="pay-razorpay-btn">
                <CreditCard className="w-4 h-4" /> {launchingRp ? "Opening…" : `Pay ₹${status.fee} securely`}
              </Button>
            )
          ) : (
            // ===== UPI deep-link fallback =====
            <>
              {confirming ? (
                <Button disabled className="w-full mt-6 h-12 text-base bg-emerald-500 text-white gap-2" data-testid="pay-confirming">
                  <CheckCircle2 className="w-4 h-4 animate-pulse" /> Confirming payment…
                </Button>
              ) : (
                <Button onClick={autoConfirm} className="w-full mt-6 h-12 text-base bg-emerald-500 hover:bg-emerald-600 text-white gap-2" data-testid="pay-im-done-btn">
                  <CheckCircle2 className="w-4 h-4" /> I&apos;ve Completed the Payment
                </Button>
              )}
              <Button variant="outline" onClick={relaunchUpi} className="w-full mt-2 gap-2" data-testid="pay-reopen-upi">
                <ExternalLink className="w-4 h-4" /> Reopen UPI App
              </Button>
            </>
          )}
        </Card>

        <button onClick={cancel} className="w-full text-sm text-slate-500 hover:text-rose-600 flex items-center justify-center gap-2 py-3" data-testid="pay-cancel-btn">
          <XCircle className="w-4 h-4" /> Cancel payment
        </button>
      </main>
    </div>
  );
}
